import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AUTH_CONFIG } from '@/lib/auth0';
import { setAuthTokenAccessor, setLogoutAction } from '@/api/mutators/IgniteAxiosClient';
import {
  setLogoutAction as globalSetLogoutAction,
  setAuthTokenAccessor as setGlobalAuthTokenAccessor,
} from '@/api/mutators/GlobalAxiosClient';
import type { AppAuthContext } from '@/types/auth';

interface UseAuthTokenBootstrapResult {
  authTokenReady: boolean;
  authTokenError: Error | null;
  retry: () => void;
}

/**
 * A hook that manages the authentication token bootstrap process.
 *
 * @param auth - The authentication context.
 * @returns An object containing the authentication token status and a retry function.
 */
export const useAuthTokenBootstrap = (auth: AppAuthContext): UseAuthTokenBootstrapResult => {
  const [authTokenReady, setReady] = useState(false);
  const [authTokenError, setError] = useState<Error | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const { isAuthenticated, isLoading, getAccessTokenSilently, user, logout } = auth;

  useEffect(() => {
    if (!isAuthenticated || isLoading || authTokenReady) return;

    const bootstrap = async () => {
      try {
        const token = await getAccessTokenSilently({ authorizationParams: AUTH_CONFIG });
        setAuthTokenAccessor(() => Promise.resolve(token));
        setGlobalAuthTokenAccessor(() => Promise.resolve(token));

        if (user) {
          const { sub, email, name } = user as any;
          Sentry.setUser({ id: sub, email: email ?? undefined, username: name ?? undefined });
        }

        const logoutHandler = async () => {
          Sentry.setUser(null);
          setAuthTokenAccessor(() => null);
          setGlobalAuthTokenAccessor(() => null);
          await logout({ logoutParams: { returnTo: import.meta.env.VITE_AUTH0_LOGIN_SPA_URL } });
        };

        setLogoutAction(logoutHandler);
        globalSetLogoutAction(logoutHandler);
        setError(null);
        setReady(true);
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Failed to retrieve access token'));
      }
    };

    void bootstrap();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, user, logout, authTokenReady, retryToken]);

  /**
   * Clear Sentry user context on logout
   */
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      Sentry.setUser(null);
      setReady(false); // reset for potential future login
    }
  }, [isLoading, isAuthenticated]);

  /**
   * Retry function to re-attempt token fetch.
   * Resetting authTokenReady causes the bootstrap effect to re-run.
   */
  const retry = () => {
    setReady(false);
    setError(null);
    setRetryToken((t) => t + 1);
  };

  return { authTokenReady, authTokenError, retry };
};
